import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2, Sun, Moon, Sparkles, Scroll, CalendarDays, User, Compass, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { StarField } from "../components/StarField";
import { ZodiacWheel } from "../components/ZodiacWheel";
import { KundliChart } from "../components/KundliChart";
import { BaziTable } from "../components/BaziTable";
import { DetailedAnalysis } from "../components/DetailedAnalysis";
import { ExportButtons } from "../components/ExportButtons";

function renderMarkdown(text) {
  if (!text) return null;
  return text.split("\n").filter((l) => l.trim() && !/^-{3,}$/.test(l.trim())).map((line, i) => {
    if (line.startsWith("### ")) return <h3 key={i} className="text-xl font-serif gold-text mt-4 mb-1">{line.slice(4)}</h3>;
    if (line.startsWith("## ")) return <h2 key={i}>{line.slice(3)}</h2>;
    if (line.startsWith("# ")) return <h2 key={i}>{line.slice(2)}</h2>;
    const html = line.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
    return <p key={i} dangerouslySetInnerHTML={{ __html: html }} />;
  });
}

const REPORTS = [
  { kind: "daily", label: "Daily Horoscope", icon: CalendarDays, desc: "Today's transits, read across all three systems." },
  { kind: "personality", label: "Personality Report", icon: User, desc: "Who you are, woven from Vedic, Hellenistic & BaZi." },
  { kind: "life_events", label: "Major Life Events", icon: Compass, desc: "Turning points & life-stage forecasts." },
];

export default function ChartDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [chart, setChart] = useState(null);
  const [tab, setTab] = useState("wheels");
  const [reports, setReports] = useState({});
  const [genLoading, setGenLoading] = useState(null);
  const [genError, setGenError] = useState({});
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    api.get(`/charts/${id}`)
      .then((r) => {
        setChart(r.data);
        const rp = r.data.reports || {};
        const existing = {};
        if (rp.personality) existing.personality = rp.personality;
        if (rp.life_events) existing.life_events = rp.life_events;
        setReports(existing);
      })
      .catch(() => { toast.error("Chart not found"); navigate("/"); });
  }, [id, navigate]);

  const generate = async (kind) => {
    setTab(kind);
    if (kind !== "daily" && reports[kind]) return;
    setGenError((e) => ({ ...e, [kind]: null }));
    setGenLoading(kind);
    try {
      const { data } = await api.post(`/charts/${id}/report/${kind}`);
      if (data.status === "ready") {
        setReports((r) => ({ ...r, [kind]: data.content }));
        setGenLoading(null);
      } else {
        pollReport(kind, 0);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setGenLoading(null);
    }
  };

  const pollReport = (kind, attempt) => {
    const tick = async () => {
      try {
        const { data } = await api.get(`/charts/${id}/report/${kind}`);
        if (data.status === "ready") {
          setReports((r) => ({ ...r, [kind]: data.content }));
          setGenLoading(null);
          return;
        }
        if (data.status === "error") {
          setGenError((e) => ({ ...e, [kind]: data.error || "Generation failed" }));
          setGenLoading(null);
          return;
        }
      } catch { /* keep polling */ }
      if (attempt >= 45) {
        setGenError((e) => ({ ...e, [kind]: "This is taking longer than expected. Please try again." }));
        setGenLoading(null);
        return;
      }
      setTimeout(() => pollReport(kind, attempt + 1), 4000);
    };
    setTimeout(tick, 4000);
  };

  if (!chart) {
    return <div className="min-h-screen cosmic-bg flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin gold-text" /></div>;
  }

  const { vedic, hellenistic, chinese } = chart.charts;

  return (
    <div className="min-h-screen cosmic-bg grain relative pb-24">
      <StarField count={60} />
      <header className="glass sticky top-0 z-40 border-b border-white/10">
        <div className="mx-auto max-w-6xl px-6 py-4 flex items-center gap-4">
          <button onClick={() => navigate("/")} data-testid="back-btn" className="text-white/60 hover:text-white transition-colors"><ArrowLeft className="h-5 w-5" /></button>
          <div>
            <h1 className="text-2xl font-serif leading-none">{chart.name}</h1>
            <p className="text-xs text-white/40 mt-1">{chart.birth_date} · {chart.birth_time} · {chart.birth_place}</p>
          </div>
          <div className="ml-auto">
            <ExportButtons base={`/charts/${chart.id}`} name={`AstroTrinity_${chart.name}`} />
          </div>
        </div>
      </header>

      <main className="relative z-10 mx-auto max-w-6xl px-6 py-10">
        <div className="flex flex-wrap gap-2 mb-10">
          <button onClick={() => setTab("wheels")} data-testid="tab-wheels"
            className={`rounded-full px-5 py-2.5 text-sm font-medium transition-colors duration-300 border ${tab === "wheels" ? "bg-white/10 border-white/20 text-white" : "border-white/10 text-white/50 hover:text-white"}`}>Birth Chart</button>
          {REPORTS.map((r) => (
            <button key={r.kind} onClick={() => generate(r.kind)} data-testid={`tab-${r.kind}`}
              className={`rounded-full px-5 py-2.5 text-sm font-medium transition-colors duration-300 border ${tab === r.kind ? "bg-white/10 border-white/20 text-white" : "border-white/10 text-white/50 hover:text-white"}`}>{r.label}</button>
          ))}
        </div>

        {tab === "wheels" ? (
          <>
            <div className="grid gap-6 lg:grid-cols-3">
              {/* Vedic Kundli */}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-2xl p-6" data-testid="vedic-card">
                <div className="flex items-center gap-2 mb-1"><Moon className="h-4 w-4 cyan-text" strokeWidth={1.5} /><p className="overline cyan-text">Vedic · Kundli</p></div>
                <h3 className="text-2xl font-serif mb-4">Jyotish Chart</h3>
                <KundliChart vedic={vedic} />
                <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
                  <div className="rounded bg-white/5 p-2"><span className="text-white/40">Ascendant</span><br />{vedic.ascendant.sign}</div>
                  <div className="rounded bg-white/5 p-2"><span className="text-white/40">Moon Nakshatra</span><br />{vedic.moon_nakshatra}</div>
                </div>
              </motion.div>

              {/* Hellenistic circle */}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="glass rounded-2xl p-6" data-testid="hellenistic-card">
                <div className="flex items-center gap-2 mb-1"><Sun className="h-4 w-4 gold-text" strokeWidth={1.5} /><p className="overline gold-text">Hellenistic · Tropical</p></div>
                <h3 className="text-2xl font-serif mb-4">Western Chart</h3>
                <ZodiacWheel planets={hellenistic.planets} ascendant={hellenistic.ascendant} accent="#D4AF37" aspects={(hellenistic.aspects || []).filter((a) => a.aspect !== "Quincunx")} />
                <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
                  <div className="rounded bg-white/5 p-2"><span className="text-white/40">Sun Sign</span><br />{hellenistic.sun_sign}</div>
                  <div className="rounded bg-white/5 p-2"><span className="text-white/40">Rising</span><br />{hellenistic.rising_sign}</div>
                </div>
              </motion.div>

              {/* Chinese table */}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="glass rounded-2xl p-6" data-testid="chinese-card">
                <div className="flex items-center gap-2 mb-1"><Sparkles className="h-4 w-4 text-[#B47CF0]" strokeWidth={1.5} /><p className="overline" style={{ color: "#B47CF0" }}>Chinese · BaZi</p></div>
                <h3 className="text-2xl font-serif mb-1">Four Pillars</h3>
                <div className="text-3xl font-serif gold-text mb-4">{chinese.zodiac_animal}</div>
                <BaziTable chinese={chinese} compact />
              </motion.div>
            </div>

            <div className="mt-8 flex justify-center">
              <button onClick={() => setExpanded((e) => !e)} data-testid="toggle-detailed"
                className="btn-gold rounded-full px-6 py-3 text-sm font-semibold flex items-center gap-2">
                {expanded ? "Hide" : "Expand"} Detailed Analysis
                <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${expanded ? "rotate-180" : ""}`} />
              </button>
            </div>

            {expanded && (
              <div className="mt-8">
                <DetailedAnalysis charts={chart.charts} />
              </div>
            )}
          </>
        ) : (
          <div className={tab === "personality" ? "space-y-8" : "max-w-3xl"}>
            {(() => {
              const meta = REPORTS.find((r) => r.kind === tab);
              return (
                <>
                  <div className="glass rounded-2xl p-8 sm:p-10" data-testid="report-panel">
                    <div className="flex items-center gap-3 mb-2"><Scroll className="h-5 w-5 gold-text" strokeWidth={1.5} /><p className="overline gold-text">{meta.desc}</p></div>
                    <h2 className="text-4xl font-serif mb-6 tracking-tight">{meta.label}</h2>
                    {genLoading === tab ? (
                      <div className="flex flex-col items-center py-16 text-center">
                        <Loader2 className="h-8 w-8 animate-spin gold-text mb-4" />
                        <p className="text-white/50 text-sm">Consulting the stars for {chart.name}...</p>
                      </div>
                    ) : genError[tab] ? (
                      <div className="flex flex-col items-center py-14 text-center" data-testid="report-error">
                        <p className="text-white/70 mb-1">The oracle couldn't complete this reading.</p>
                        <p className="text-white/40 text-xs mb-5 max-w-md">{/budget/i.test(genError[tab]) ? "The AI credits for this app have run out. Please top up the Universal Key balance, then try again." : genError[tab]}</p>
                        <button onClick={() => generate(tab)} data-testid="report-retry" className="btn-gold rounded-full px-5 py-2.5 text-sm font-semibold">Try Again</button>
                      </div>
                    ) : reports[tab] ? (
                      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }} className="report-content" data-testid="report-content">
                        {renderMarkdown(reports[tab])}
                      </motion.div>
                    ) : (
                      <p className="text-white/50">Preparing your reading...</p>
                    )}
                  </div>

                  {tab === "personality" && reports.personality && !genLoading && (
                    <div>
                      <h3 className="text-2xl font-serif mb-5 gold-text">The Charts Behind Your Reading</h3>
                      <DetailedAnalysis charts={chart.charts} />
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}
      </main>
    </div>
  );
}
